import { Dispatch, SetStateAction, useState } from 'react';
import { CurrentUsageData, RolloverData, UsageData } from 'src/types/usage';
import { calculateRolloverData, getCurrentCycle } from 'src/utils/usage';
import useShellContext from '../useShellContext';
import UsageClient from 'src/services/UsageClient';

interface UsageClientProps {
  isFullUpgrade: boolean;
  setUsageData: Dispatch<SetStateAction<UsageData[]>>;
  setCurrentUsageData: Dispatch<SetStateAction<CurrentUsageData>>;
  setRolloverData: Dispatch<SetStateAction<RolloverData>>;
}

interface UsageClientReturn {
  loading: boolean;
  error: string | null;
  fetchAllUsageData: (
    subscriptionId: string,
    entityId: string
  ) => Promise<void>;
  fetchCurrentUsageData: (
    subscriptionId: string,
    entityId: string,
    billingCycleId: string
  ) => Promise<void>;
}

const useBillingClient = ({
  isFullUpgrade,
  setUsageData,
  setCurrentUsageData,
  setRolloverData
}: UsageClientProps): UsageClientReturn => {
  const { authProvider, stack } = useShellContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const usageClient = new UsageClient(authProvider, stack);

  const fetchCurrentUsageData = async (
    subscriptionId: string,
    entityId: string,
    billingCycleId: string
  ) => {
    setLoading(true);
    try {
      const data = await usageClient.getUsageByBillingId(
        subscriptionId,
        entityId,
        billingCycleId
      );
      setCurrentUsageData(data);
      setRolloverData(calculateRolloverData(data, isFullUpgrade));
    } catch (error) {
      setLoading(false);
      setError(error);
      console.error('Error fetching billing data:', error);
    }
  };

  const fetchAllUsageData = async (
    subscriptionId: string,
    entityId: string
  ) => {
    setLoading(true);
    try {
      const data = await usageClient.getAllUsageData(subscriptionId, entityId);
      const currentCycle = getCurrentCycle(data);
      await fetchCurrentUsageData(
        subscriptionId,
        entityId,
        currentCycle.billingCycleId || ''
      );
      setUsageData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error);
      console.error('Error fetching all usage data:', error);
    }
  };

  return {
    loading,
    error,
    fetchAllUsageData,
    fetchCurrentUsageData
  };
};

export default useBillingClient;
